import { Trash2 } from 'reicon-react';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Button } from '@/components/ui/button';
import { acaoBloqueavel, atributosDeBloqueio, type MotivoBloqueio } from '@/lib/bloqueio';
import { cn } from '@/lib/utils';
import { MEIO_PAGTO, type MeioPagtoNFe } from '../../domain/pagamento/formaPagamento';
import type { PagamentoAplicado, StatusPagamento } from '../../domain/pagamento/saldoPagamento';
import { ZERO_CENTAVOS, formatarCentavos } from '../../domain/precificacao/dinheiro';
import { useCanalDisplay } from '../../services/display/useCanalDisplay';
import { useCondicoesPagamento } from '../../services/pagamento/pagamentoQueries';
import { useFocoVendaStore } from '../../stores/focoVendaStore';
import { useSessionStore } from '../../stores/sessionStore';
import { useVendaStore } from '../../stores/vendaStore';
import { iconeDoPagamento } from './iconePorMeio';
import {
  AVISO_PAGAMENTO_DO_DOCUMENTO,
  CHAMADA_VALOR_JA_RECEBIDO,
  DESTAQUE_NFCE_SAI_SEM_O_VALOR,
} from './avisosPagamentoDoDocumento';
import {
  AVISO_DESASSOCIACAO_MANUAL,
  CHAMADA_PIX_NAO_E_CANCELADO,
  DESTAQUE_PIX_SEGUE_NO_BANCO,
} from './pix/avisosPix';
import { DialogoConfirmacaoDestrutiva } from './DialogoConfirmacaoDestrutiva';
import { ModalPix } from './pix/ModalPix';
import { ModalTef } from './tef/ModalTef';
import { JanelaEstornoTef } from './tef/JanelaEstornoTef';
import {
  CHAMADA_ESTORNO,
  DESTAQUE_ESTORNO,
  EXPLICACAO_ESTORNO,
  TITULO_CONFIRMAR_ESTORNO,
} from './tef/avisosTef';

/**
 * Bloco "Pagamentos aplicados" do cartão de pagamento (T028, `FR-011`/`FR-012`)
 * — réplica do nó `COoLE` do Pencil (`design/HTML - Pencil/CentriumCheckout.html`,
 * linhas 2830–2923), filho do cartão "Pagamento e totais" (`OzP7o`).
 *
 * Estrutura do nó, item a item: coluna de 360px com `gap: 4px`; cabeçalho
 * (`N2JOw`) com o título à esquerda (Inter 13/600, `uBHDr`) e o "Texto restante"
 * à direita (Inter 12/600 em `$danger`, `s6VSY`, texto literal "Faltante R$ …");
 * cada forma aplicada (`vmqVn`) é uma faixa de 34px, raio 12, fundo `$surface-soft`,
 * `padding: 0 12px`, com nome+ícone à esquerda (`pvAYq`, gap 8, ícone de 16px —
 * reicon, ver AD-201) e, à direita (`tNCgL`, gap 6), o valor em Geist Mono 13/600 (`AYBpH`) e
 * o botão remover de 26×26 com raio total, fundo `$surface-strong` e ícone
 * `trash-2` de 14px (`EB08g`/`cQBj5`).
 *
 * **Três decisões sem nó correspondente no `.pen`**, todas por o desenho
 * modelar um único estado (uma forma PIX aprovada, saldo ainda em aberto):
 *
 * 1. **Estado vazio: o bloco inteiro não é renderizado.** Sem pagamento não há
 *    o que listar, e um título com lista vazia por baixo é a "lista fantasma"
 *    que o desenho não desenha. Sem pagamento, o faltante é o próprio total da
 *    venda — no desktop, a métrica "Faltante" do bloco escuro (`TotalDaVenda`);
 *    na etapa 2 do celular, que não tem o bloco escuro desde AD-255, o total do
 *    rodapé da lista de itens.
 * 2. **"Texto restante" some quando o saldo está coberto.** O nó só existe no
 *    estado "falta pagar"; escrever "Faltante R$ 0,00" seria afirmar uma falta
 *    que não existe.
 * 3. **Todo meio tem ícone**, vindo de `iconePorMeio.ts` — o mesmo mapa que o
 *    combobox de forma usa. O Pencil só nomeia três (`qr-code`, `banknote`,
 *    `credit-card`, no nó "Métodos de pagamento rápidos"); os outros 18 são
 *    inferidos, por decisão do usuário (2026-09-04). A versão anterior deixava
 *    os inferidos **sem ícone**, o que produzia faixas visualmente diferentes na
 *    mesma lista sem que a diferença dissesse nada ao operador.
 */
export function ListaPagamentosAplicados(): ReactElement | null {
  const pagamentos = useVendaStore((estado) => estado.pagamentos);
  const removerPagamento = useVendaStore((estado) => estado.removerPagamento);
  const confirmarEstornoTef = useVendaStore((estado) => estado.confirmarEstornoTef);
  // Só o campo, não o objeto `SaldoPagamento`: `saldo()` monta um objeto novo a
  // cada chamada, e devolvê-lo do seletor daria uma referência diferente por
  // render — o Zustand v5 trataria como mudança e o componente entraria em laço.
  const saldoRestante = useVendaStore((estado) => estado.saldo().saldoRestante);

  /**
   * Remoção de PIX aguardando confirmação (item 3 do usuário, 2026-09-04).
   *
   * Guarda o `idPagamento`, e não o `PagamentoAplicado`: o objeto do estado é
   * substituído a cada mutação do slice, e uma cópia congelada aqui poderia
   * confirmar a remoção de uma versão que não existe mais. O `id` é estável.
   */
  const [idAConfirmar, setIdAConfirmar] = useState<string | null>(null);

  /**
   * Estorno de TEF aprovado (feature 010, US3): primeiro a confirmação, depois
   * a janela. Dois estados, e não um, porque cancelar a confirmação não pode
   * deixar rastro, e a janela só existe depois do "sim". Guardam o `id` pelo
   * mesmo motivo de `idAConfirmar`.
   */
  const [idAEstornar, setIdAEstornar] = useState<string | null>(null);
  const [idEstornando, setIdEstornando] = useState<string | null>(null);

  const cobrancaPix = usePixPendente();
  const cobrancaTef = useTefPendente();

  /**
   * A lista salta para a forma recém-inserida (pedido do usuário, 2026-09-04).
   *
   * A rolagem agora mora **dentro** desta lista, não na coluna do cartão: numa
   * venda com muitas formas, a última inserida nasceria fora da área visível, e
   * o operador não teria confirmação nenhuma de que a inserção aconteceu — o
   * campo de valor apenas se esvaziaria. O gesto que ele faria à mão (rolar até
   * o fim) é o que o efeito faz por ele.
   *
   * A dependência é o **id da última forma**, não o tamanho do array nem o array
   * inteiro: desde AD-163 remover não tira o item da lista, só o marca
   * `EXCLUIDO`, então o comprimento não distingue mais inserção de exclusão, e
   * `pagamentos` muda de referência a cada mutação do slice (confirmação de PIX,
   * recusa de TEF) — rolar em todas elas arrancaria a lista de onde o operador a
   * deixou para reler uma forma anterior. Só uma forma nova no fim muda esse id.
   *
   * `scrollTop = scrollHeight` em vez de `scrollIntoView` no último `<li>`:
   * `scrollIntoView` rola **todo** ancestral rolável até o elemento, incluindo a
   * coluna do cartão, que só rola em tela baixa demais — e nesse caso ela
   * arrastaria junto o seletor de condição e o campo de valor para fora da vista.
   * Aqui o alvo é explicitamente esta lista, e nada mais se move.
   */
  const listaRef = useRef<HTMLUListElement>(null);
  const idUltimoPagamento = pagamentos.at(-1)?.idPagamento ?? null;

  useEffect(() => {
    const lista = listaRef.current;
    if (lista === null) {
      return;
    }
    lista.scrollTop = lista.scrollHeight;
  }, [idUltimoPagamento]);

  if (pagamentos.length === 0) {
    return null;
  }

  /**
   * Remover PIX — ou uma forma vinda do documento — pede confirmação; qualquer
   * outra sai direto.
   *
   * A forma importada entrou aqui pela retomada de um rascunho já pago
   * (AD-169): o valor foi recebido por outro operador e está gravado no
   * documento dentro do ERP. Removê-la em silêncio é o mesmo estrago do
   * "Limpar" do cabeçalho, alcançado por outro botão — guardar só um dos dois
   * seria um freio que o operador contorna sem perceber.
   *
   * O desvio acontece **aqui**, e não dentro do slice, porque o slice não pode
   * abrir janela nem esperar por uma resposta do operador sem virar assíncrono —
   * e `removerPagamento` é chamada por outros caminhos (a limpeza pós-entrega da
   * 004, por exemplo) que não devem perguntar nada a ninguém. A regra de negócio
   * ("PIX pode sair da venda") mora no slice; a pergunta mora na tela.
   *
   * O log é preservado nos dois caminhos: quem registra
   * `FORMA_PAGAMENTO_REMOVIDA` é `removerPagamento`, e a confirmação só decide
   * **se** ela é chamada.
   */
  // Qual das duas confirmações abrir: a do valor já recebido tem precedência
  // sobre a do PIX, mas na prática as duas nunca coincidem — o que vem do
  // documento entra com `integracao: 'NENHUMA'`.
  const estornando =
    idEstornando === null
      ? undefined
      : pagamentos.find((pagamento) => pagamento.idPagamento === idEstornando);

  const aConfirmarVeioDeDocumento =
    idAConfirmar !== null &&
    (pagamentos.find((pagamento) => pagamento.idPagamento === idAConfirmar)?.veioDeDocumento ??
      false);

  function pedirRemocao(idPagamento: string): void {
    const alvo = pagamentos.find((pagamento) => pagamento.idPagamento === idPagamento);
    if (alvo === undefined) {
      return;
    }
    // TEF aprovado não sai por `removerPagamento` — o slice o recusa (I6,
    // `FR-003`). A saída é o estorno pelo ERP, que pergunta antes e só risca a
    // forma quando a SmartTEF devolve `EST` (feature 010, `research.md` D14).
    if (alvo.integracao === 'TEF' && alvo.status === 'APROVADO') {
      setIdAEstornar(idPagamento);
      return;
    }
    if (alvo.veioDeDocumento || alvo.integracao === 'PIX_DINAMICO') {
      setIdAConfirmar(idPagamento);
      return;
    }
    removerPagamento(idPagamento);
  }

  /*
   * A seção usa `min-h-min` (min-content), não `min-h-0`: o piso dela é
   * derivado do próprio conteúdo mínimo — cabeçalho mais **uma** faixa, que é o
   * piso declarado no `ul` abaixo. Não cresce com o número de pagamentos,
   * porque a altura mínima do `ul` é a que ele mesmo declara, e não a soma das
   * faixas.
   *
   * Com `min-h-0` a seção colapsava a zero junto com a lista, e as faixas
   * escapavam para fora da coluna do cartão (ver o comentário do `ul`).
   */
  return (
    <section className="flex min-h-min w-full flex-col gap-xxs" data-testid="pagamentos-aplicados">
      {/* O faltante é o valor e não quebra; quem cede na tela estreita é o
          título (revisão de quebras de linha, 2026-09-24). */}
      <header className="flex w-full shrink-0 items-center justify-between gap-xs">
        <h3 className="min-w-0 text-base font-semibold text-foreground">Pagamentos aplicados</h3>
        {saldoRestante > 0 ? (
          <span
            className="shrink-0 text-sm font-semibold whitespace-nowrap text-destructive"
            data-testid="pagamentos-saldo-restante"
          >
            Faltante {formatarCentavos(saldoRestante)}
          </span>
        ) : null}
      </header>

      {/* **Só esta lista rola** (pedido do usuário, 2026-09-04). Antes a barra
          nascia na coluna inteira do cartão: crescer a lista empurrava condição,
          desconto e forma para cima e para fora da vista, e o operador rolava um
          bloco de controles para chegar a uma lista logo abaixo deles. Agora a
          lista é o único bloco da coluna com `min-h-0`, então é ela que o flex
          escolhe para absorver a falta de espaço — encolhe até a altura
          disponível e ganha a barra por dentro, com todo o resto do cartão
          parado.

          `overflow-x-hidden` junto do `overflow-y-auto`, nunca sozinho, pelo
          mesmo motivo já anotado na coluna do cartão (`PainelPagamentoETotais`):
          pelo CSS, `overflow-x: visible` ao lado de um `overflow-y` não-visível
          é computado como `auto`, e a primeira faixa a estourar 1px criaria uma
          barra horizontal aqui dentro.

          `-mx-1 px-1` devolve, por dentro, a folga que esse corte tirava do anel
          de `focus-visible` do botão de remover — ele se desenha 3px para fora
          da borda e apareceria recortado nas laterais. A lista fica 8px mais
          larga que a área útil (avançando sob o `p-base` do cartão) e recupera
          os mesmos 8px como padding: as faixas mantêm a largura de antes e
          continuam alinhadas com os blocos de cima. */}
      {/* `min-h-[34px]` — exatamente uma faixa — é um **piso**, não um tamanho.
          Sem ele a lista era o único bloco flexível de uma coluna cujos outros
          blocos são todos `shrink-0`: em 1280×720 (o PDV real, e o viewport do
          E2E) condição, desconto, forma e valor consomem a coluna inteira, e o
          flex entregava altura **zero** à lista. As faixas então escapavam para
          fora do container e eram cobertas pelo bloco de total, que vem depois
          no fluxo — o pagamento aparecia na tela, mas "Remover" ficava
          intocável, com o clique caindo em `total-da-venda` (achado ao
          diagnosticar `pagamento-pix.spec.ts`, 2026-09-04).

          Com o piso, quando não há espaço a coluna do cartão estoura e usa o
          próprio `overflow-y-auto` — a "rede de segurança" que
          `PainelPagamentoETotais` já declarava e que até aqui nunca era
          acionada, porque a lista absorvia toda a falta de espaço colapsando.
          O bloco de total e o botão de finalizar não se movem: são irmãos da
          coluna rolável, não filhos dela. */}
      <ul
        ref={listaRef}
        className="-mx-1 flex min-h-[34px] flex-col gap-xxs overflow-x-hidden overflow-y-auto px-1"
      >
        {pagamentos.map((pagamento) => (
          <ItemPagamentoAplicado
            key={pagamento.idPagamento}
            pagamento={pagamento}
            onRemover={pedirRemocao}
          />
        ))}
      </ul>

      {cobrancaPix}
      {cobrancaTef}

      {idAEstornar !== null && (
        <DialogoConfirmacaoDestrutiva
          testId="confirmar-estorno-tef"
          titulo={TITULO_CONFIRMAR_ESTORNO}
          subtitulo="O pagamento já foi aprovado no TEF"
          chamada={CHAMADA_ESTORNO}
          explicacao={EXPLICACAO_ESTORNO}
          destaque={DESTAQUE_ESTORNO}
          rotuloConfirmar="Estornar"
          onConfirmar={() => {
            setIdEstornando(idAEstornar);
            setIdAEstornar(null);
          }}
          onCancelar={() => {
            setIdAEstornar(null);
          }}
        />
      )}

      {estornando !== undefined && estornando.dadosTEF !== null && (
        <JanelaEstornoTef
          key={estornando.idPagamento}
          paymentIdentifier={estornando.dadosTEF.pagId}
          valor={estornando.valorAplicado}
          onEstornado={() => {
            confirmarEstornoTef(estornando.idPagamento);
          }}
          onFechar={() => {
            setIdEstornando(null);
          }}
        />
      )}

      {idAConfirmar !== null &&
        (aConfirmarVeioDeDocumento ? (
          <DialogoConfirmacaoDestrutiva
            testId="confirmar-remocao-documento"
            titulo="Remover o pagamento vindo do documento?"
            subtitulo="Esta venda foi retomada já paga"
            chamada={CHAMADA_VALOR_JA_RECEBIDO}
            explicacao={AVISO_PAGAMENTO_DO_DOCUMENTO}
            destaque={DESTAQUE_NFCE_SAI_SEM_O_VALOR}
            rotuloConfirmar="Remover mesmo assim"
            onConfirmar={() => {
              removerPagamento(idAConfirmar);
              setIdAConfirmar(null);
            }}
            onCancelar={() => {
              setIdAConfirmar(null);
            }}
          />
        ) : (
          <DialogoConfirmacaoDestrutiva
            testId="confirmar-remocao-pix"
            titulo="Remover o PIX da venda?"
            subtitulo="A cobrança já foi gerada no banco"
            chamada={CHAMADA_PIX_NAO_E_CANCELADO}
            explicacao={AVISO_DESASSOCIACAO_MANUAL}
            destaque={DESTAQUE_PIX_SEGUE_NO_BANCO}
            rotuloConfirmar="Remover mesmo assim"
            onConfirmar={() => {
              removerPagamento(idAConfirmar);
              setIdAConfirmar(null);
            }}
            onCancelar={() => {
              setIdAConfirmar(null);
            }}
          />
        ))}
    </section>
  );
}

/**
 * Ponto de disparo real da integração PIX (T024, feature 009) — substitui o stub
 * no-op de `iniciarIntegracao` deixado pela feature 008
 * (`vendaStore.pagamentoDepsPadrao`).
 *
 * **Por que a janela nasce do estado, e não de um callback.** O contrato da 008
 * entrega a integração por `iniciarIntegracao(integracao, ctx)`, uma porta
 * imperativa chamada de dentro do slice. Montar o modal a partir dela exigiria
 * guardar o contexto em algum lugar — um estado paralelo ao `PagamentoAplicado`,
 * capaz de discordar dele. Aqui a janela é uma **função** do pagamento que está
 * `PENDENTE_INTEGRACAO` com `integracao === 'PIX_DINAMICO'`: existe exatamente
 * enquanto esse pagamento existir, e some no instante em que ele é confirmado ou
 * removido. O stub em `vendaStore` continua no-op de propósito — não há segundo
 * mecanismo de disparo (`tasks.md`, nota final).
 *
 * `find`, não `filter`: `PENDENTE_INTEGRACAO` bloqueia a inserção da próxima
 * forma até resolver, então há no máximo um por vez (invariante J1 da 009).
 *
 * O `idPagamento` fica **fechado dentro dos callbacks**, e não copiado para
 * dentro da cobrança: é o vínculo único entre a janela e o pagamento que a
 * originou (ver `domain/pix/cobrancaPix.ts`).
 *
 * ---
 *
 * ### Por que a janela seguiu o `idPagamento` em vez de "existe um pendente?"
 *
 * Desde 2026-09-04 a janela permanece 10 segundos na tela **depois** da
 * aprovação (pedido do usuário — ver o TSDoc de `ModalPix`). O pagamento deixa
 * de ser `PENDENTE_INTEGRACAO` no instante em que o polling confirma, então a
 * condição antiga ("existe um pagamento pendente de PIX?") desmontaria a janela
 * exatamente no quadro em que ela precisa mostrar que deu certo.
 *
 * A correção é mínima e preserva a decisão de AD-158: a janela continua sendo
 * função do estado, só que do **pagamento que ela está exibindo**, seguido por
 * `id`. Um pendente novo entra em cena; a saída é sempre `onFechar`, e nunca um
 * segundo estado de "aberto" capaz de discordar do slice — se o pagamento sumir
 * da lista (recusa, remoção), a janela some junto, sem gesto nenhum.
 */
/**
 * Saída de uma janela de integração (PIX ou TEF) com a venda já coberta: o
 * foco vai para "Finalizar venda" (pedido do usuário, 2026-10-02, AD-262).
 *
 * É a mesma regra do Enter no valor recebido (`EntradaPagamento`), que não
 * alcança estas formas: o pagamento entra `PENDENTE_INTEGRACAO`, que não conta
 * no saldo (`FR-004`), então no instante da inserção ainda falta valor e o foco
 * volta ao campo. A cobrança só se encerra quando a janela aprovada fecha.
 *
 * Chamado **junto** do `setIdExibido(null)`, no mesmo lote: o React roda a
 * limpeza do modal que sai — `useFocoDeModal` devolve o foco ao campo de valor —
 * antes dos efeitos novos, e o do `BotaoFinalizarVenda` vem por último e fica
 * com o foco. A desistência e a recusa passam por aqui também, mas com saldo
 * restante: aí nada é pedido e a devolução ao campo prevalece.
 */
function focarFinalizacaoSeVendaCoberta(): void {
  if (useVendaStore.getState().saldo().saldoRestante === ZERO_CENTAVOS) {
    useFocoVendaStore.getState().focarFinalizarVenda();
  }
}

function usePixPendente(): ReactElement | null {
  const pagamentos = useVendaStore((estado) => estado.pagamentos);
  const clienteAtual = useVendaStore((estado) => estado.clienteAtual);
  const confirmarPagamentoIntegrado = useVendaStore((estado) => estado.confirmarPagamentoIntegrado);
  const recusarPagamentoIntegrado = useVendaStore((estado) => estado.recusarPagamentoIntegrado);
  const registrarIdentificadorIntegracao = useVendaStore(
    (estado) => estado.registrarIdentificadorIntegracao,
  );
  const [idExibido, setIdExibido] = useState<string | null>(null);

  // O catálogo já está em cache (`staleTime` de 30 min, `PAY-01`): a mesma query
  // alimenta o combobox de forma ao lado, e chegar aqui exige ter aplicado uma
  // forma dele. Sem dado carregado o piso é zero, isto é, sem bloqueio por valor
  // mínimo — recusar a cobrança por um dado que ainda não chegou seria pior do
  // que deixar o próprio ERP recusá-la.
  const catalogo = useCondicoesPagamento();
  const minimoPix = catalogo.data?.minimoPix ?? ZERO_CENTAVOS;
  // Envio por WhatsApp contratado pela empresa (`isWhatsappEnabled`, AD-258).
  // Sem bootstrap, ou com o ERP anterior ao campo, vale desabilitado.
  const whatsappHabilitado = useSessionStore(
    (estado) => estado.registro?.SessaoUsuario.isWhatsappEnabled === true,
  );

  /**
   * Ponte para a tela do cliente (feature 015). Fica aqui, e não dentro do
   * `ModalPix`, porque é aqui que já se conhece o React e o store — o modal
   * recebe uma função e a chama, sem saber que existe uma segunda aba.
   *
   * O hook é chamado incondicionalmente, antes de qualquer retorno antecipado:
   * o canal só nasce na primeira publicação, então uma venda sem PIX não abre
   * `BroadcastChannel` nenhum.
   */
  const publicarNoDisplay = useCanalDisplay();

  const pendente = pagamentos.find(
    (pagamento) =>
      pagamento.status === 'PENDENTE_INTEGRACAO' && pagamento.integracao === 'PIX_DINAMICO',
  );

  // Entrada em cena durante o render, não em `useEffect` (mesmo padrão de
  // `codigoCondicaoAnterior` em `PainelPagamentoETotais`): o efeito abriria a
  // janela um quadro depois, deixando a tela de venda visível por um instante
  // com um pagamento pendente que ninguém está resolvendo. O `set` converge —
  // no render seguinte os dois `id` são iguais e nada mais muda.
  if (pendente !== undefined && pendente.idPagamento !== idExibido) {
    setIdExibido(pendente.idPagamento);
  }

  // Segue o pagamento **exibido**, em qualquer status: é o que mantém a janela
  // de pé durante os 10 segundos do estado aprovado. Some sozinha quando o
  // pagamento deixa a lista (recusa/remoção) — `find` devolve `undefined`.
  const exibido =
    idExibido === null
      ? undefined
      : pagamentos.find((pagamento) => pagamento.idPagamento === idExibido);

  if (exibido === undefined) {
    return null;
  }

  return (
    <ModalPix
      // A janela é recriada do zero a cada pagamento: sem a `key`, um segundo
      // PIX na mesma venda reaproveitaria o componente montado e as travas de
      // "uma geração por montagem" impediriam a nova cobrança.
      key={exibido.idPagamento}
      formaCodigo={exibido.formaCodigo}
      valor={exibido.valorAplicado}
      minimoPix={minimoPix}
      whatsappHabilitado={whatsappHabilitado}
      clienteAtual={clienteAtual}
      onAprovado={(pixGuid) => {
        confirmarPagamentoIntegrado(exibido.idPagamento, { pixGuid });
      }}
      onAbandonado={(motivo) => {
        recusarPagamentoIntegrado(exibido.idPagamento, motivo);
      }}
      onCobrancaGerada={(pixGuid) => {
        registrarIdentificadorIntegracao(exibido.idPagamento, { pixGuid });
      }}
      onFechar={() => {
        // Único caminho de saída da janela — o automático de 10s depois da
        // aprovação, o `X`, o ESC (só com o pagamento aprovado) e a desistência
        // confirmada convergem todos aqui.
        setIdExibido(null);
        focarFinalizacaoSeVendaCoberta();
      }}
      onEstadoDisplay={publicarNoDisplay}
    />
  );
}

/**
 * Ponto de disparo da cobrança no TEF (T027, feature 010) — espelho de
 * `usePixPendente`, com o mesmo raciocínio de AD-158: a janela é **função** do
 * pagamento `PENDENTE_INTEGRACAO` com `integracao === 'TEF'`, seguido pelo
 * `idPagamento` exibido para continuar de pé durante os 10s do estado aprovado.
 * `iniciarIntegracao` segue no-op; não há segundo gatilho.
 *
 * `find`, não `filter`: há no máximo uma janela de integração aberta por vez —
 * ela é modal e travada (`research.md` D15). Vários TEFs na mesma venda entram
 * em sequência, cada um com a sua janela, recriada pela `key`.
 *
 * Copiado, não generalizado com o do PIX: as duas janelas recebem props sem
 * nada em comum além dos callbacks, e uma abstração "integração pendente"
 * acoplaria duas features que divergem exatamente no estorno.
 */
function useTefPendente(): ReactElement | null {
  const pagamentos = useVendaStore((estado) => estado.pagamentos);
  const clienteAtual = useVendaStore((estado) => estado.clienteAtual);
  // `CondicaoPrazo` (= `PraNumPar`, o número de parcelas da condição). A condição
  // não muda com pagamento vivo na venda (I9 da 008), então é estável aqui.
  const prazoDaCondicao = useVendaStore((estado) => estado.condicaoSelecionada?.prazo ?? 0);
  const confirmarPagamentoIntegrado = useVendaStore((estado) => estado.confirmarPagamentoIntegrado);
  const recusarPagamentoIntegrado = useVendaStore((estado) => estado.recusarPagamentoIntegrado);
  const registrarIdentificadorIntegracao = useVendaStore(
    (estado) => estado.registrarIdentificadorIntegracao,
  );
  // Só a presença: o valor do `UsuarioGAM` fica no cookie do BFF, que o insere
  // no corpo de `CriarCardPagamento` (AD-224, invariante T8).
  const usuarioGamPresente = useSessionStore(
    (estado) => (estado.registro?.SessaoUsuario.UsuarioGAM?.trim() ?? '') !== '',
  );
  const [idExibido, setIdExibido] = useState<string | null>(null);

  const pendente = pagamentos.find(
    (pagamento) => pagamento.status === 'PENDENTE_INTEGRACAO' && pagamento.integracao === 'TEF',
  );

  // Entrada em cena durante o render, pelo mesmo motivo de `usePixPendente`.
  if (pendente !== undefined && pendente.idPagamento !== idExibido) {
    setIdExibido(pendente.idPagamento);
  }

  const exibido =
    idExibido === null
      ? undefined
      : pagamentos.find((pagamento) => pagamento.idPagamento === idExibido);

  if (exibido === undefined) {
    return null;
  }

  return (
    <ModalTef
      key={exibido.idPagamento}
      formaCodigo={exibido.formaCodigo}
      meioPagtoNFe={exibido.meioPagtoNFe}
      valor={exibido.valorAplicado}
      prazoDaCondicao={prazoDaCondicao}
      clienteAtual={clienteAtual}
      usuarioGamPresente={usuarioGamPresente}
      onAprovado={(dadosTEF) => {
        confirmarPagamentoIntegrado(exibido.idPagamento, { dadosTEF });
      }}
      onAbandonado={(motivo) => {
        recusarPagamentoIntegrado(exibido.idPagamento, motivo);
      }}
      onCobrancaCriada={(tefPagId) => {
        registrarIdentificadorIntegracao(exibido.idPagamento, { tefPagId });
      }}
      onFechar={() => {
        setIdExibido(null);
        focarFinalizacaoSeVendaCoberta();
      }}
    />
  );
}

interface ItemPagamentoAplicadoProps {
  readonly pagamento: PagamentoAplicado;
  readonly onRemover: (idPagamento: string) => void;
}

/**
 * Uma faixa da lista — o nó `vmqVn` ("PIX aplicado") do Pencil.
 *
 * **Excluída fica riscada, mas não sai da lista** (pedido do usuário,
 * 2026-09-04) — mesmo tratamento do item cancelado do carrinho (`GridItens`,
 * `linha.cancelada`): `text-muted-foreground line-through`, texto `sr-only`
 * anunciando o estado para leitor de tela, e o botão de remover some (nada a
 * remover de novo). A forma excluída não vai ao ERP — `montarPagamentosParaPayload`
 * só envia `APROVADO` —, mas o log de inserção/exclusão continua no array de
 * eventos, escrito por `removerPagamento` antes de a UI sequer saber que a
 * exclusão aconteceu.
 */
function ItemPagamentoAplicado({ pagamento, onRemover }: ItemPagamentoAplicadoProps): ReactElement {
  const Icone = iconeDoPagamento(pagamento);
  const nome = ROTULO_POR_MEIO[pagamento.meioPagtoNFe];
  const anotacao = anotacaoDoPagamento(pagamento);
  const motivoBloqueio = motivoBloqueioRemocao(pagamento);
  const excluido = pagamento.status === 'EXCLUIDO';

  return (
    <li
      className={cn(
        // `shrink-0`: a lista virou área rolável e encolhe quando o cartão
        // aperta. Sem isto o flex tiraria a altura das próprias faixas — as 34px
        // do desenho viram 20 e nada rola, porque o conteúdo "cabe" espremido.
        'flex h-[34px] w-full shrink-0 items-center justify-between rounded-lg bg-muted px-sm',
        excluido && 'text-muted-foreground line-through',
      )}
      data-testid="pagamento-aplicado"
      data-id-pagamento={pagamento.idPagamento}
      data-status={pagamento.status}
    >
      <span className="flex min-w-0 items-center gap-xs">
        <Icone className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="truncate text-base font-semibold text-foreground">{nome}</span>
        {anotacao === null ? null : (
          <span
            className="shrink-0 text-sm font-medium text-muted-foreground"
            data-testid="pagamento-anotacao"
          >
            {anotacao}
          </span>
        )}
        {excluido ? <span className="sr-only"> (pagamento excluído)</span> : null}
      </span>

      <span className="flex shrink-0 items-center gap-[6px]">
        <span
          className="font-mono text-base font-semibold tabular-nums text-foreground"
          data-testid="pagamento-valor"
        >
          {/* O que o operador entregou nesta forma, não o que ela abateu da
              venda (correção do usuário, 2026-09-10): `valorAplicado` é
              limitado ao saldo por `derivarValores`, então a linha de um
              dinheiro de R$ 100 numa venda de R$ 50 exibia R$ 50 — o valor da
              venda no lugar do que o operador digitou. O excedente continua
              aparecendo como "Troco" nos totais, e `valorAplicado` segue sendo
              o que vai ao ERP. `??` porque só dinheiro carrega `valorRecebido`
              (I3). */}
          {formatarCentavos(pagamento.valorRecebido ?? pagamento.valorAplicado)}
        </span>
        {/* Não há mais botão de vale aqui. O vale devolução deixou de ser algo
            aplicado **sobre** um pagamento e passou a ser a própria forma de
            pagamento (`FpgUtiCar = 'VDV'`), inserida pelo modal que abre ao
            escolher essa forma. O ticket aparece nesta faixa como a anotação
            "Vale <código>", ao lado do nome. */}
        {/* Bloqueio explicativo, nunca `disabled` nativo (`lib/bloqueio.ts`).
            Desde a feature 010 nenhuma forma aprovada é bloqueada aqui: o PIX
            e o TEF passam pela confirmação de `pedirRemocao` — o TEF abre o
            estorno. Só sobra o TEF aprovado sem `dadosTEF`, que não tem com
            que estornar (`motivoBloqueioRemocao`). */}
        {excluido ? null : (
          <Button
            type="button"
            variant="secondary"
            size="icon-xs"
            className="size-[26px] shrink-0 rounded-full text-muted-foreground"
            data-testid="remover-pagamento"
            aria-label={`Remover ${nome}`}
            {...atributosDeBloqueio(motivoBloqueio)}
            onClick={acaoBloqueavel(motivoBloqueio, () => {
              onRemover(pagamento.idPagamento);
            })}
          >
            <Trash2 className="size-3.5" aria-hidden="true" />
          </Button>
        )}
      </span>
    </li>
  );
}

/**
 * I6, reescrita duas vezes.
 *
 * - **2026-09-04 (usuário):** só o TEF aprovado bloqueava a remoção; o PIX passou
 *   a sair com confirmação, porque o Checkout nunca soube cancelá-lo (J5) e
 *   travar a forma não protegia dinheiro nenhum.
 * - **Feature 010 (AD-259):** o TEF aprovado também deixou de ser bloqueio. A
 *   remoção **direta** continua proibida — `removerPagamento` a recusa —, mas o
 *   botão agora abre a confirmação de estorno, e a forma só é riscada quando a
 *   SmartTEF devolve `EST` (`confirmarEstornoTef`). A frase que este bloqueio
 *   exibia ("cancele a transação no terminal") mandava o operador para uma
 *   saída que deixou de ser a do Checkout.
 *
 * O único bloqueio que sobra é o TEF aprovado **sem** `dadosTEF`: sem o
 * `pagId` não há o que consultar nem estornar. Não deveria existir — a 010
 * sempre grava os dados ao aprovar —, e por isso o motivo manda para o ERP.
 */
function motivoBloqueioRemocao(pagamento: PagamentoAplicado): MotivoBloqueio {
  if (
    pagamento.status !== 'APROVADO' ||
    pagamento.integracao !== 'TEF' ||
    pagamento.dadosTEF !== null
  ) {
    return null;
  }
  return 'Este cartão aprovado no TEF não tem a identificação da transação para estornar: confira o estorno no ERP.';
}

/**
 * Texto curto à direita do nome. Aprovado sem vale não recebe nada — é o estado
 * que o Pencil desenha, e ele fica idêntico ao nó. Os outros dois estados são
 * invisíveis no desenho e precisam se distinguir: `PENDENTE_INTEGRACAO` **não**
 * conta no saldo (`FR-004`/`FR-005`), e uma faixa igual à de um pagamento
 * aprovado faria o operador acreditar que a venda já está coberta.
 */
function anotacaoDoPagamento(pagamento: PagamentoAplicado): string | null {
  const status = ANOTACAO_POR_STATUS[pagamento.status];
  if (status !== null) {
    return status;
  }
  return pagamento.ticketDevolucao === null ? null : `Vale ${pagamento.ticketDevolucao}`;
}

const ANOTACAO_POR_STATUS: Record<StatusPagamento, string | null> = {
  APROVADO: null,
  PENDENTE_INTEGRACAO: 'Aguardando',
  RECUSADO: 'Recusado',
  // `null`, como `APROVADO`: quem comunica a exclusão é o riscado + o
  // `sr-only` da faixa, não um texto que substituiria a anotação "Vale
  // <código>" — o operador ainda precisa ver qual vale ficou de fora.
  EXCLUIDO: null,
};

/**
 * `PagamentoAplicado` guarda o **meio** da NFCe, não a descrição da forma
 * cadastrada — por isso o nome exibido é traduzido aqui, e não lido do catálogo:
 * o catálogo pode ter mudado desde a aplicação, e o pagamento nunca resolve seus
 * dados olhando o catálogo depois (`data-model.md` §2, "Regra de fronteira").
 *
 * O rótulo de `Pix` é "PIX" em caixa alta, como o nó `Q2wLdo` do Pencil.
 */
const ROTULO_POR_MEIO: Record<MeioPagtoNFe, string> = {
  [MEIO_PAGTO.Dinheiro]: 'Dinheiro',
  [MEIO_PAGTO.Cheque]: 'Cheque',
  [MEIO_PAGTO.CartaoCredito]: 'Cartão de crédito',
  [MEIO_PAGTO.CartaoDebito]: 'Cartão de débito',
  [MEIO_PAGTO.CreditoLoja]: 'Crédito da loja',
  [MEIO_PAGTO.ValeAlimentacao]: 'Vale alimentação',
  [MEIO_PAGTO.ValeRefeicao]: 'Vale refeição',
  [MEIO_PAGTO.ValePresente]: 'Vale presente',
  [MEIO_PAGTO.ValeCombustivel]: 'Vale combustível',
  [MEIO_PAGTO.DuplicataMercantil]: 'Duplicata mercantil',
  [MEIO_PAGTO.BoletoBancario]: 'Boleto bancário',
  [MEIO_PAGTO.DepositoBancario]: 'Depósito bancário',
  [MEIO_PAGTO.Pix]: 'PIX',
  [MEIO_PAGTO.TransferenciaBancaria]: 'Transferência bancária',
  [MEIO_PAGTO.ProgramaFidelidade]: 'Programa de fidelidade',
  [MEIO_PAGTO.PixEstatico]: 'PIX estático',
  [MEIO_PAGTO.CreditoEmLoja]: 'Crédito em loja',
  [MEIO_PAGTO.PagamentoNaoInformado]: 'Pagamento não informado',
  [MEIO_PAGTO.SemPagamento]: 'Sem pagamento',
  [MEIO_PAGTO.PagamentoPosterior]: 'Pagamento posterior',
  [MEIO_PAGTO.Outros]: 'Outros',
};
